import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Download, FileText, Users, Shield, Clock, MapPin, Phone } from "lucide-react";

const TeenConsentForm = ({ onClose }) => {
  const generatePDF = () => {
    // Create the consent form content
    const formContent = `
PARENT/GUARDIAN CONSENT FORM FOR TEEN RIDER USE OF DELEVR

Created on: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}

================================================================================

I, ___________________________ (Parent/Guardian Name), hereby consent to my teen 
child, ___________________________ (Teen Name), born on __________ (DOB), using 
Delevr Rideshare services as a rider.

ACKNOWLEDGMENTS:

[ ] I confirm that my child is at least 16 years old.

[ ] I acknowledge that Delevr requires teen riders (ages 16-17) to have parent 
    or guardian consent and agree to the policies set forth by Delevr.

[ ] I understand that my child must adhere to all ride restrictions (e.g., 
    specified hours, approved pickup/dropoff locations).

[ ] I agree that Delevr is a technology platform and that all rides are provided 
    by independent drivers, and Delevr is not a transportation carrier.

[ ] I accept that Delevr will not assume liability for my teen's actions during 
    the ride, and I, as a parent/guardian, assume all responsibility.

[ ] I acknowledge that I can monitor rides in real-time through the app and will 
    contact Delevr support if I have concerns.

[ ] I certify that the information provided is accurate and that I will update 
    Delevr if any circumstances change.

================================================================================

SIGNATURES:

Parent/Guardian Signature: ___________________________

Date: ___________________________

Contact Information: ___________________________

Delevr Account Email (Parent/Guardian): ___________________________

================================================================================

DELEVR RIDESHARE - TEEN RIDER POLICY
Effective Date: April 25, 2026

1. ELIGIBILITY
Teen riders must be at least 16 years old to use Delevr services. Any rider 
under 18 must adhere to additional requirements below.

2. PARENTAL/GUARDIAN CONSENT
- Riders aged 16 or 17 must have explicit parental or guardian consent to 
  create an account and use the platform.
- Parents or guardians must acknowledge and agree to these terms on behalf 
  of the teen.

3. RIDE RESTRICTIONS
- Teen riders may only request rides during specified hours (e.g., no late-night 
  rides, depending on jurisdiction).
- Teen rides must originate and end at locations pre-approved by a parent or 
  guardian (e.g., school, home, or approved addresses).

4. RIDE MONITORING
- Parents or guardians can set up real-time ride tracking through their own 
  Delevr account, ensuring visibility of the route and ETA.
- Any safety concerns should be reported immediately via Delevr support.

5. DRIVER REQUIREMENTS
- Drivers accepting rides for teen riders must have additional vetting and be 
  specifically cleared by Delevr.
- Drivers must not engage in any inappropriate conduct, and all communication 
  must remain professional.

6. LIABILITY
- Parents or guardians acknowledge that Delevr is a platform connecting 
  independent drivers with riders and that Delevr does not assume liability 
  for teen rider conduct.
- Parents or guardians assume all responsibility for their teen's actions 
  during the ride.

7. COMPLIANCE
- This policy complies with applicable minor protection laws.
- Delevr reserves the right to suspend or terminate teen accounts if policy 
  requirements are violated.

================================================================================

For questions or concerns, contact:
Delevr Rideshare LLC
Atlanta, Georgia
support@delevr.com

================================================================================
`;

    // Create a Blob with the content
    const blob = new Blob([formContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    // Create download link
    const link = document.createElement('a');
    link.href = url;
    link.download = 'DELEVR_Teen_Rider_Consent_Form.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="bg-white rounded-2xl shadow-lg max-w-2xl mx-auto">
      <CardHeader className="bg-gradient-to-r from-[#1E4DB7] to-[#2D5FC9] text-white rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div>
            <CardTitle className="text-xl">Teen Rider Consent Form</CardTitle>
            <CardDescription className="text-white/80">Required for riders aged 16-17</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {/* Policy Highlights */}
        <div className="space-y-3">
          <h4 className="font-semibold text-slate-900">Policy Highlights</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
              <Shield className="w-5 h-5 text-[#1E4DB7] shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Age Requirement</p>
                <p className="text-xs text-slate-500">Must be 16-17 years old</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
              <Clock className="w-5 h-5 text-[#1E4DB7] shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Ride Hours</p>
                <p className="text-xs text-slate-500">Restricted late-night rides</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
              <MapPin className="w-5 h-5 text-[#1E4DB7] shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Approved Locations</p>
                <p className="text-xs text-slate-500">Parent-approved pickup/dropoff</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
              <Phone className="w-5 h-5 text-[#1E4DB7] shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Real-time Tracking</p>
                <p className="text-xs text-slate-500">Parents can monitor rides</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form Description */}
        <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">What's Included</p>
              <ul className="text-sm text-amber-700 mt-1 space-y-1">
                <li>• Parent/Guardian consent acknowledgments</li>
                <li>• Teen Rider Policy (Effective April 25, 2026)</li>
                <li>• Signature and contact fields</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Download Button */}
        <Button 
          data-testid="download-teen-consent-form"
          onClick={generatePDF}
          className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-[#1E4DB7] font-semibold rounded-full h-12"
        >
          <Download className="w-5 h-5 mr-2" /> Download Consent Form
        </Button>

        <p className="text-xs text-slate-500 text-center">
          Please print, complete, and keep a copy for your records. 
          Upload the signed form during teen account registration.
        </p>

        {onClose && (
          <Button 
            variant="ghost" 
            onClick={onClose}
            className="w-full rounded-full"
          >
            Close
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default TeenConsentForm;
